import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  UsersRound, Plus, Pencil, Trash2, Search, Link2, Unlink, UserPlus,
  Heart, Handshake, Briefcase, Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const RELATIONSHIP_TYPES = [
  { value: "family", label: "Family", icon: Heart, color: "bg-rose-100 text-rose-700" },
  { value: "spouse", label: "Spouse / Partner", icon: Heart, color: "bg-pink-100 text-pink-700" },
  { value: "co_investor", label: "Co-Investor", icon: Handshake, color: "bg-blue-100 text-blue-700" },
  { value: "business_partner", label: "Business Partner", icon: Briefcase, color: "bg-amber-100 text-amber-700" },
];

const emptyForm = {
  name: "", relationship_type: "family", notes: "",
  combined_budget_min: "", combined_budget_max: "", decision_timeline: "",
};

const FamilyGroupsPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState("");
  const [leadSearch, setLeadSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/family-groups`);
      setGroups(res.data);
    } catch (err) {
      toast.error("Failed to load family groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const openCreate = () => {
    setEditingGroup(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditingGroup(g);
    setForm({
      name: g.name || "",
      relationship_type: g.relationship_type || "family",
      notes: g.notes || "",
      combined_budget_min: g.combined_budget_min || "",
      combined_budget_max: g.combined_budget_max || "",
      decision_timeline: g.decision_timeline || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Group name is required"); return; }
    const payload = {
      ...form,
      combined_budget_min: form.combined_budget_min ? parseFloat(form.combined_budget_min) : null,
      combined_budget_max: form.combined_budget_max ? parseFloat(form.combined_budget_max) : null,
    };
    try {
      if (editingGroup) {
        await axios.put(`${API}/family-groups/${editingGroup.group_id}`, payload);
        toast.success("Group updated");
      } else {
        await axios.post(`${API}/family-groups`, payload);
        toast.success("Group created");
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save group");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this family group?")) return;
    try {
      await axios.delete(`${API}/family-groups/${id}`);
      toast.success("Group deleted");
      fetchGroups();
    } catch (err) {
      toast.error("Failed to delete group");
    }
  };

  const openLinkDialog = (group) => {
    setSelectedGroup(group);
    setLeadSearch("");
    setSearchResults([]);
    setLinkDialogOpen(true);
  };

  const searchLeads = async () => {
    if (!leadSearch.trim()) return;
    try {
      const res = await axios.get(`${API}/leads?search=${leadSearch}&limit=10`);
      setSearchResults(res.data.leads || []);
    } catch (err) {
      toast.error("Search failed");
    }
  };

  const linkLead = async (leadId) => {
    try {
      await axios.post(`${API}/family-groups/${selectedGroup.group_id}/link`, { lead_id: leadId });
      toast.success("Lead linked to group");
      fetchGroups();
      // Refresh selected group
      const updated = await axios.get(`${API}/family-groups/${selectedGroup.group_id}`);
      setSelectedGroup(updated.data);
    } catch (err) {
      toast.error("Failed to link lead");
    }
  };

  const unlinkLead = async (groupId, leadId) => {
    try {
      await axios.delete(`${API}/family-groups/${groupId}/link/${leadId}`);
      toast.success("Lead unlinked");
      fetchGroups();
      if (selectedGroup?.group_id === groupId) {
        const updated = await axios.get(`${API}/family-groups/${groupId}`);
        setSelectedGroup(updated.data);
      }
    } catch (err) {
      toast.error("Failed to unlink lead");
    }
  };

  const filtered = groups.filter(g =>
    g.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRelType = (v) => RELATIONSHIP_TYPES.find(r => r.value === v) || RELATIONSHIP_TYPES[0];

  const formatINR = (amount) => {
    if (!amount) return "N/A";
    if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)} Cr`;
    if (amount >= 100000) return `${(amount / 100000).toFixed(1)} L`;
    return amount.toLocaleString("en-IN");
  };

  const getScoreBadge = (score) => {
    const styles = {
      hot: "bg-red-100 text-red-700", warm: "bg-amber-100 text-amber-700",
      nurture: "bg-blue-100 text-blue-700", new: "bg-emerald-100 text-emerald-700",
    };
    return styles[score] || "bg-slate-100 text-slate-500";
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="family-groups-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Family & Decision Groups</h1>
            <p className="text-sm text-slate-500 mt-1">Link related leads — families, spouses, co-investors</p>
          </div>
          <Button onClick={openCreate} data-testid="create-family-group-btn">
            <Plus className="w-4 h-4 mr-2" /> New Group
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {RELATIONSHIP_TYPES.map((rt) => {
            const count = groups.filter(g => g.relationship_type === rt.value).length;
            const Icon = rt.icon;
            return (
              <Card key={rt.value}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${rt.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">{rt.label}</p>
                    <p className="text-xl font-bold text-slate-900">{count}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search groups..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10" data-testid="family-groups-search" />
        </div>

        {/* Groups Grid */}
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading groups...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-400">
              <UsersRound className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No family groups found. Link related leads together.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((group) => {
              const relType = getRelType(group.relationship_type);
              const Icon = relType.icon;
              const members = group.members || [];
              return (
                <Card key={group.group_id} className="hover:shadow-md transition-shadow" data-testid={`family-group-card-${group.group_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${relType.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{group.name}</CardTitle>
                          <Badge variant="outline" className={`mt-1 text-xs ${relType.color}`}>{relType.label}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(group)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(group.group_id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {group.notes && <p className="text-xs text-slate-500">{group.notes}</p>}
                    {(group.combined_budget_min || group.combined_budget_max) && (
                      <p className="text-xs text-slate-600">
                        Budget: {formatINR(group.combined_budget_min)} - {formatINR(group.combined_budget_max)}
                      </p>
                    )}
                    {group.decision_timeline && (
                      <p className="text-xs text-slate-500">Timeline: {group.decision_timeline}</p>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-slate-600">Members ({members.length})</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openLinkDialog(group)}
                          data-testid={`link-leads-${group.group_id}`}>
                          <UserPlus className="w-3 h-3 mr-1" /> Link Lead
                        </Button>
                      </div>
                      {members.length === 0 ? (
                        <p className="text-xs text-slate-400">No members linked yet</p>
                      ) : (
                        <div className="space-y-1.5">
                          {members.map((m) => (
                            <div key={m.lead_id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1.5">
                              <div className="flex items-center gap-2">
                                <button className="text-blue-600 hover:underline font-medium" onClick={() => navigate(`/leads/${m.lead_id}`)}>
                                  {m.name}
                                </button>
                                <Badge variant="outline" className={`text-[10px] ${getScoreBadge(m.score)}`}>{m.score || "new"}</Badge>
                                <span className="text-slate-400 capitalize">{m.stage}</span>
                              </div>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400"
                                onClick={() => unlinkLead(group.group_id, m.lead_id)}>
                                <Unlink className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="family-group-dialog">
            <DialogHeader>
              <DialogTitle>{editingGroup ? "Edit Group" : "New Family/Decision Group"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Group Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Sharma Family" data-testid="family-group-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Relationship Type</label>
                <Select value={form.relationship_type} onValueChange={(v) => setForm({ ...form, relationship_type: v })}>
                  <SelectTrigger data-testid="family-group-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIP_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Combined Budget Min</label>
                  <Input type="number" value={form.combined_budget_min} onChange={(e) => setForm({ ...form, combined_budget_min: e.target.value })}
                    placeholder="e.g. 5000000" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Combined Budget Max</label>
                  <Input type="number" value={form.combined_budget_max} onChange={(e) => setForm({ ...form, combined_budget_max: e.target.value })}
                    placeholder="e.g. 10000000" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Decision Timeline</label>
                <Input value={form.decision_timeline} onChange={(e) => setForm({ ...form, decision_timeline: e.target.value })}
                  placeholder="e.g. 3 months, Immediate" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes about this group..." rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="family-group-save-btn">{editingGroup ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Lead Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="link-lead-dialog">
            <DialogHeader>
              <DialogTitle>Link Lead — {selectedGroup?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex gap-2">
                <Input placeholder="Search by name or phone..." value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchLeads()}
                  data-testid="link-lead-search" />
                <Button onClick={searchLeads} variant="outline" data-testid="link-lead-search-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {searchResults.length === 0 ? (
                  <p className="p-4 text-center text-sm text-slate-400">Search for leads to link</p>
                ) : (
                  searchResults.map((lead) => {
                    const alreadyLinked = selectedGroup?.member_lead_ids?.includes(lead.lead_id);
                    return (
                      <div key={lead.lead_id} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.phone} - {lead.stage}</p>
                        </div>
                        {alreadyLinked ? (
                          <Badge variant="outline" className="text-xs text-emerald-600">Linked</Badge>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => linkLead(lead.lead_id)}
                            data-testid={`link-btn-${lead.lead_id}`}>
                            <Link2 className="w-3 h-3 mr-1" /> Link
                          </Button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FamilyGroupsPage;
